import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { errorHandler, successHandler } from "../../../helpers/handlers";
import api from "../../../config/axios";
import { logout } from "../auth";

const programmeListKey = (postId, isDraft) =>
  `${postId}:${Boolean(isDraft)}`;

const initialState = {
  data: [],
  lastDocId: null,
  pageLoading: false,
  actionLoading: false,
  error: null,
  hasMoreContent: false,
  totalCount: null,
  /** Bumps on each "first page" fetch so stale async responses are ignored. */
  listContextKey: null,
};

const slice = createSlice({
  name: "posts",
  initialState,
  reducers: {
    setSelectedPost: (state, action) => {
      state.selectedPost = action.payload;
    },
    storeCurrentTab: (state, action) => {
      state.currentTab = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(logout.fulfilled, () => initialState)

      // Fetching programme — clear list on first page so tab/post switches do not
      // show the previous rows while the new request is in flight.
      .addCase(fetchProgrammes.pending, (state, action) => {
        state.pageLoading = true;
        const lastDocId = action.meta.arg.lastDocId ?? "";
        if (lastDocId === "") {
          const { postId, isDraft } = action.meta.arg;
          state.data = [];
          state.lastDocId = null;
          state.hasMoreContent = false;
          state.totalCount = null;
          state.listContextKey = programmeListKey(postId, isDraft);
        }
      })
      .addCase(fetchProgrammes.fulfilled, (state, action) => {
        const arg = action.meta.arg;
        const key = programmeListKey(arg.postId, arg.isDraft);
        if (key !== state.listContextKey) {
          return;
        }

        const { data, hasMoreContent } = action.payload;
        if (action.payload.reset) {
          state.data = [];
        }
        state.data = [
          ...state.data.filter(
            (existingItem) =>
              !data.data.some((newItem) => newItem.id === existingItem.id)
          ),
          ...data.data,
        ];
        state.lastDocId = data.lastDocId;
        state.hasMoreContent = hasMoreContent;
        if (typeof data.total === "number") {
          state.totalCount = data.total;
        }

        state.pageLoading = false;
        state.error = null;
      })
      .addCase(fetchProgrammes.rejected, (state, action) => {
        const arg = action.meta.arg;
        const key = programmeListKey(arg.postId, arg.isDraft);
        if (key !== state.listContextKey) {
          return;
        }
        state.error = action.payload;
        state.pageLoading = false;
      })

      // Creating a programme
      .addCase(createProgramme.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(createProgramme.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.data.splice(0, 0, action.payload);
        if (typeof state.totalCount === "number") {
          state.totalCount += 1;
        }
      })
      .addCase(createProgramme.rejected, (state, action) => {
        state.error = action.payload;
        state.actionLoading = false;
      })

      // Updating a programme
      .addCase(updateProgramme.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(updateProgramme.fulfilled, (state, action) => {
        state.actionLoading = false;
        const index = state.data.findIndex(
          (programme) => programme.id === action.payload.id
        );

        if (index !== -1) {
          state.data[index] = action.payload;
        }
      })
      .addCase(updateProgramme.rejected, (state, action) => {
        state.error = action.payload;
        state.actionLoading = false;
      })

      // Deleting a Programme
      .addCase(deleteProgramme.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(deleteProgramme.fulfilled, (state, action) => {
        state.data = state.data.filter((post) => post.id !== action.payload.id);
        if (typeof state.totalCount === "number") {
          state.totalCount = Math.max(0, state.totalCount - 1);
        }
        state.actionLoading = false;
      })
      .addCase(deleteProgramme.rejected, (state) => {
        state.actionLoading = false;
      })

      .addCase(publishProgramme.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(publishProgramme.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(publishProgramme.rejected, (state) => {
        state.actionLoading = false;
      })

      .addCase(batchPublishProgrammes.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(batchPublishProgrammes.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(batchPublishProgrammes.rejected, (state) => {
        state.actionLoading = false;
      })

      .addCase(batchDeleteProgrammes.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(batchDeleteProgrammes.fulfilled, (state, action) => {
        state.actionLoading = false;
        const deletedIds = (action.payload?.results || [])
          .filter((r) => r.success === true)
          .map((r) => r.id);
        if (deletedIds.length > 0) {
          state.data = state.data.filter((row) => !deletedIds.includes(row.id));
          if (typeof state.totalCount === "number") {
            state.totalCount = Math.max(0, state.totalCount - deletedIds.length);
          }
        }
      })
      .addCase(batchDeleteProgrammes.rejected, (state) => {
        state.actionLoading = false;
      })

      .addCase(batchRefineProgrammes.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(batchRefineProgrammes.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(batchRefineProgrammes.rejected, (state) => {
        state.actionLoading = false;
      });
  },
});

// Fetch programmes by postId
export const fetchProgrammes = createAsyncThunk(
  "programmes/fetch",
  async (
    { postId, isDraft = false, lastDocId = "", limit = 10 },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.get(
        `/admin/programmes?postId=${postId}&isDraft=${isDraft}&limit=${limit}&lastDocId=${lastDocId}`
      );
      return {
        data: response.data.payload,
        reset: lastDocId === "",
        hasMoreContent: response.data.payload.data.length === limit,
      };
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

// Create programme
export const createProgramme = createAsyncThunk(
  "programme/create",
  async ({ schoolIsDraft, saveAsDraft, data }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/admin/programmes?isDraft=${schoolIsDraft}&saveAsDraft=${saveAsDraft}`,
        data
      );
      successHandler(response.data.message);
      return response.data.payload;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

// Update a programme
export const updateProgramme = createAsyncThunk(
  "programmes/update",
  async ({ id, data, isDraft = false }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `/admin/programmes/${id}?isDraft=${isDraft}`,
        data
      );
      successHandler(response.data.message);
      return response.data.payload;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

// Delete a programme
export const deleteProgramme = createAsyncThunk(
  "programmes/delete",
  async ({ id, isDraft }, { rejectWithValue }) => {
    try {
      const response = await api.delete(
        `/admin/programmes/${id}?isDraft=${isDraft}`
      );
      successHandler(response.data.message);
      return { id };
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

export const publishProgramme = createAsyncThunk(
  "programmes/publish",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/admin/programmes/${id}/publish`);
      successHandler(response.data.message);
      return response.data.payload;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

const BATCH_PUBLISH_MAX = 50;

/** POST /admin/programmes/batch-publish — chunks into 50 ids per request. */
export const batchPublishProgrammes = createAsyncThunk(
  "programmes/batchPublish",
  async ({ ids }, { rejectWithValue }) => {
    try {
      const unique = [...new Set((ids || []).filter(Boolean))];
      if (unique.length === 0) {
        return {
          attempted: 0,
          succeeded: 0,
          failed: 0,
          results: [],
        };
      }

      const merged = {
        attempted: 0,
        succeeded: 0,
        failed: 0,
        results: [],
      };

      for (let i = 0; i < unique.length; i += BATCH_PUBLISH_MAX) {
        const chunk = unique.slice(i, i + BATCH_PUBLISH_MAX);
        const response = await api.post("/admin/programmes/batch-publish", {
          ids: chunk,
        });
        const p = response.data.payload || {};
        merged.attempted += p.attempted ?? chunk.length;
        merged.succeeded += p.succeeded ?? 0;
        merged.failed += p.failed ?? 0;
        if (Array.isArray(p.results)) {
          merged.results = merged.results.concat(p.results);
        }
      }

      return merged;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

const BATCH_DELETE_MAX = 50;

/** POST /admin/programmes/batch-delete — chunks into 50 ids per request. */
export const batchDeleteProgrammes = createAsyncThunk(
  "programmes/batchDelete",
  async ({ ids, isDraft = true, articleId }, { rejectWithValue }) => {
    try {
      const unique = [...new Set((ids || []).filter(Boolean))];
      if (unique.length === 0) {
        return {
          attempted: 0,
          succeeded: 0,
          failed: 0,
          results: [],
        };
      }

      const merged = {
        attempted: 0,
        succeeded: 0,
        failed: 0,
        results: [],
      };

      const articleQuery =
        articleId != null && String(articleId).trim()
          ? `&articleId=${encodeURIComponent(String(articleId).trim())}`
          : "";

      for (let i = 0; i < unique.length; i += BATCH_DELETE_MAX) {
        const chunk = unique.slice(i, i + BATCH_DELETE_MAX);
        const response = await api.post(
          `/admin/programmes/batch-delete?isDraft=${Boolean(isDraft)}${articleQuery}`,
          { ids: chunk }
        );
        const p = response.data.payload || {};
        merged.attempted += p.attempted ?? chunk.length;
        merged.succeeded += p.succeeded ?? 0;
        merged.failed += p.failed ?? 0;
        if (Array.isArray(p.results)) {
          merged.results = merged.results.concat(p.results);
        }
      }

      successHandler("Batch delete completed.");
      return merged;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

const BATCH_REFINE_MAX = 50;

/** POST /admin/programmes/batch-refine — chunks into 50 ids per request. */
export const batchRefineProgrammes = createAsyncThunk(
  "programmes/batchRefine",
  async ({ ids, extraUserInstructions = "", save = true }, { rejectWithValue }) => {
    try {
      const unique = [...new Set((ids || []).filter(Boolean))];
      if (unique.length === 0) {
        return {
          attempted: 0,
          succeeded: 0,
          failed: 0,
          results: [],
        };
      }

      const merged = {
        attempted: 0,
        succeeded: 0,
        failed: 0,
        results: [],
      };

      for (let i = 0; i < unique.length; i += BATCH_REFINE_MAX) {
        const chunk = unique.slice(i, i + BATCH_REFINE_MAX);
        const payload = { ids: chunk, save };
        const instructions =
          typeof extraUserInstructions === "string"
            ? extraUserInstructions.trim()
            : "";
        if (instructions !== "") {
          payload.extraUserInstructions = instructions;
        }
        const response = await api.post("/admin/programmes/batch-refine", payload);
        const p = response.data.payload || {};
        merged.attempted += p.attempted ?? chunk.length;
        merged.succeeded += p.succeeded ?? 0;
        merged.failed += p.failed ?? 0;
        if (Array.isArray(p.results)) {
          merged.results = merged.results.concat(p.results);
        }
      }

      successHandler("Batch refine completed.");
      return merged;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

export default slice.reducer;

export const { setSelectedPost, storeCurrentTab } = slice.actions;
